import type { BoAOfferConfig } from './types';

export enum BoAOffers {
  SoFiCheckingAndSavings = '473eb46a-aefa-11ec-be17-4b2c08a5fefe',
  SoFiCheckingAndSavingsCheckingPages = 'e7d5eefa-8dd7-11ec-ad2e-4f5ed2cb1a85',
  SoFiCheckingAndSavingsNoDirectDeposit = '7437d8dc-cbd5-11ec-9610-139705f54ee0',
  ChimeCheckingAccount = '784175c2-259f-11e8-9c02-678c036160c2',
  AlliantUltimateOpportunitySavings = '9c0c0842-7d34-11eb-b5f3-dbe5c837a741',
  Alliant6MonthCd = '48f2635a-b6e3-11ed-a049-cb467c0eef07',
  Alliant1YearCd = 'dde33d80-259e-11e8-9c02-db20990fa7cf',
  Alliant3YearCd = 'd63b037e-259e-11e8-9c02-13e24d20abbd',
  Marcus6MonthCd = 'e18fc124-259e-11e8-9c02-cb0e347b3620',
  Marcus7MonthNoPenaltyCd = '2d2c8c74-49a8-11e9-9778-93f5b440de29',
  Marcus9MonthCd = '8c0f2256-480f-11e8-9c02-5fbf379113a8',
  Marcus1YearCd = 'ca2a18fe-259e-11e8-9c02-af9fbea5a8e4',
  Marcus13MonthNoPenaltyCd = '1e598816-4136-11e9-9778-e7df068a34d8',
  Marcus14MonthHighYieldCd = '17e11588-9680-11ec-8658-5fffe5d1e85b',
  Marcus3YearCd = 'd305b3e8-259e-11e8-9c02-e361c48f869d',
  Marcus5YearCd = 'd82eb3c4-259e-11e8-9c02-a38a63b516fc',
  ChaseTotalChecking = '7841917e-259f-11e8-9c02-9f89679d816a',
}

export enum BoAInstitutions {
  Alliant = '8d880e56-259e-11e8-9c02-5f0ce3471639',
  Chase = '8d8875b2-259e-11e8-9c02-0f4e733deec0',
  Chime = '73154344-259f-11e8-9c02-9f8c67a07c3d',
  Marcus = '8d8860f4-259e-11e8-9c02-7b70f250df90',
  SoFi = '1795c4a6-3c84-11e9-9778-cbb9025f977f',
}

const BEST_OF_AWARDS_OFFER_CONFIG: Record<BoAOffers, BoAOfferConfig> = {
  [BoAOffers.SoFiCheckingAndSavings]: { awardText: '2026 Best Bank' },
  [BoAOffers.SoFiCheckingAndSavingsCheckingPages]: { awardText: '2026 Best Bank' },
  [BoAOffers.SoFiCheckingAndSavingsNoDirectDeposit]: { awardText: '2026 Best Bank' },
  [BoAOffers.ChimeCheckingAccount]: {
    awardText: '2026 Best Checking Account',
    awardTextOverrideForReviewPage: '2026 Best Online Banking Experience',
    showBannerOnFullReviewSection: true,
  },
  [BoAOffers.AlliantUltimateOpportunitySavings]: { awardText: '2026 Best Credit Union' },
  [BoAOffers.Alliant6MonthCd]: { awardText: '2026 Best Credit Union' },
  [BoAOffers.Marcus7MonthNoPenaltyCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Alliant1YearCd]: { awardText: '2026 Best Credit Union' },
  [BoAOffers.Alliant3YearCd]: { awardText: '2026 Best Credit Union' },
  [BoAOffers.Marcus6MonthCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus9MonthCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus1YearCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus13MonthNoPenaltyCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus14MonthHighYieldCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus3YearCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.Marcus5YearCd]: { awardText: '2026 Best Bank for CDs' },
  [BoAOffers.ChaseTotalChecking]: { awardText: '2026 Best Bank for Branch Access' },
} as const;

const BEST_OF_AWARDS_ROUNDUP_CONFIG: Record<string, (BoAOffers | BoAInstitutions)[]> = {
  'checking-accounts': [
    BoAOffers.SoFiCheckingAndSavingsCheckingPages,
    BoAOffers.ChimeCheckingAccount,
  ],
  'high-yield-online-savings-accounts': [BoAOffers.SoFiCheckingAndSavings],
  'savings-accounts': [BoAOffers.SoFiCheckingAndSavings],
  'online-checking-accounts': [BoAOffers.SoFiCheckingAndSavingsCheckingPages],
  'bank-bonuses-and-promotions': [
    BoAOffers.SoFiCheckingAndSavingsNoDirectDeposit,
    BoAOffers.ChimeCheckingAccount,
    BoAOffers.AlliantUltimateOpportunitySavings,
    BoAOffers.ChaseTotalChecking,
  ],
  'cd-rates': [
    BoAInstitutions.Marcus,
    BoAInstitutions.Alliant,
    BoAOffers.Marcus6MonthCd,
    BoAOffers.Marcus9MonthCd,
    BoAOffers.Marcus1YearCd,
    BoAOffers.Marcus13MonthNoPenaltyCd,
    BoAOffers.Marcus14MonthHighYieldCd,
    BoAOffers.Marcus3YearCd,
    BoAOffers.Marcus5YearCd,
    BoAOffers.Alliant6MonthCd,
    BoAOffers.Alliant1YearCd,
    BoAOffers.Alliant3YearCd,
    BoAOffers.Marcus7MonthNoPenaltyCd,
  ],
} as const;

const BEST_OF_AWARDS_REVIEW_CONFIG: Partial<Record<BoAInstitutions | BoAOffers, BoAOfferConfig>> = {
  [BoAInstitutions.Alliant]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.Alliant1YearCd],
  [BoAInstitutions.Chase]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.ChaseTotalChecking],
  [BoAInstitutions.Chime]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.ChimeCheckingAccount],
  [BoAOffers.ChimeCheckingAccount]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.ChimeCheckingAccount],
  [BoAInstitutions.Marcus]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.Marcus1YearCd],
  [BoAInstitutions.SoFi]: BEST_OF_AWARDS_OFFER_CONFIG[BoAOffers.SoFiCheckingAndSavings],
} as const;

export function getBestOfAwardsRoundupConfig(slug: string, id: string): BoAOfferConfig | null {
  const idConfig = BEST_OF_AWARDS_ROUNDUP_CONFIG[slug] ?? [];

  if (idConfig.includes(id as BoAOffers | BoAInstitutions)) {
    return BEST_OF_AWARDS_OFFER_CONFIG[id as BoAOffers] ?? getBestOfAwardsReviewConfig(id) ?? null;
  }

  return null;
}

export function getBestOfAwardsReviewConfig(id: string): BoAOfferConfig | null {
  return BEST_OF_AWARDS_REVIEW_CONFIG[id as BoAInstitutions] ?? null;
}
